import { ArrowClockwiseIcon, CopyIcon } from '@phosphor-icons/react'
import { useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { SwitchFormField } from '@/components/form/switch-form-field'
import { Button } from '@/components/ui/button'
import { Divider } from '@/components/ui/divider'
import { EmptyState } from '@/components/ui/empty-state'
import { Icon } from '@/components/ui/icon'
import { Typography } from '@/components/ui/typography'

import type { PackageRoomTabProps, PackagesSettingsForm } from '../packages-settings.types'
import { getCarrierAccessCodeTip, getMaskUsernameTip } from './package-room-tab.constants'
import * as S from './package-room-tab.styled'

export const PackageRoomTab = ({
  settings,
  isRegenerating,
  onCopyToClipboard,
  onRegenerateLink,
}: PackageRoomTabProps) => {
  const { t } = useTranslation(['packages-settings'])
  const packageRooms = settings.packageRooms ?? []
  const legacyUrl = (settings as unknown as { packageRoomUrl?: string }).packageRoomUrl

  const carrierAccessCodeEnabled = useWatch<PackagesSettingsForm, 'carrierAccessCodeEnabled'>({
    name: 'carrierAccessCodeEnabled',
  })
  const maskUsernameEnabled = useWatch<PackagesSettingsForm, 'maskUsernameEnabled'>({
    name: 'maskUsernameEnabled',
  })

  return (
    <S.Container>
      <S.SwitchContainer>
        <SwitchFormField
          name="carrierAccessCodeEnabled"
          label={t('packages-settings:packageRoomTab.carrierAccessCodeEnabled.label')}
          tooltip={getCarrierAccessCodeTip(t)}
          inlineLabel={
            carrierAccessCodeEnabled
              ? t('packages-settings:common.yes')
              : t('packages-settings:common.no')
          }
        />

        <Divider color="gray500" />

        <SwitchFormField
          name="maskUsernameEnabled"
          label={t('packages-settings:packageRoomTab.maskUsernameEnabled.label')}
          tooltip={getMaskUsernameTip(t)}
          inlineLabel={
            maskUsernameEnabled
              ? t('packages-settings:common.yes')
              : t('packages-settings:common.no')
          }
        />
      </S.SwitchContainer>

      {packageRooms.length > 0 ? (
        packageRooms.map((room) => (
          <S.RoomItem key={room.id}>
            <Typography size="text-medium" weight="medium" color="primary800">
              {room.name}
            </Typography>
            <S.RoomContent>
              <S.RoomUrl size="text-medium" color="primary800">
                {room.url}
              </S.RoomUrl>
              <S.RoomActions>
                <Button
                  onlyBox
                  use="secondary"
                  size="small"
                  disabled={isRegenerating}
                  onClick={() => onCopyToClipboard(room.url)}
                >
                  <Icon icon={CopyIcon} size="1.6rem" />
                </Button>
                <Button
                  use="secondary"
                  size="small"
                  loading={isRegenerating}
                  onClick={() => onRegenerateLink(room.id)}
                >
                  <Icon icon={ArrowClockwiseIcon} size="1.6rem" color="primary800" />
                  <Typography as="span" size="text-small" weight="medium" color="primary800">
                    {t('packages-settings:packageRoomTab.regenerateLink')}
                  </Typography>
                </Button>
              </S.RoomActions>
            </S.RoomContent>
          </S.RoomItem>
        ))
      ) : legacyUrl ? (
        <S.RoomItem>
          <Typography size="text-medium" weight="medium" color="primary800">
            {t('packages-settings:packageRoomTab.legacyRoomName')}
          </Typography>
          <S.RoomContent>
            <S.RoomUrl size="text-medium" color="primary800">
              {legacyUrl}
            </S.RoomUrl>
            <S.RoomActions>
              <Button
                onlyBox
                use="secondary"
                size="small"
                disabled={isRegenerating}
                onClick={() => onCopyToClipboard(legacyUrl)}
              >
                <Icon icon={CopyIcon} size="1.6rem" />
              </Button>
              <Button
                use="secondary"
                size="small"
                loading={isRegenerating}
                onClick={() => onRegenerateLink()}
              >
                <Icon icon={ArrowClockwiseIcon} size="1.6rem" color="primary800" />
                <Typography as="span" size="text-small" weight="medium" color="primary800">
                  {t('packages-settings:packageRoomTab.regenerateLink')}
                </Typography>
              </Button>
            </S.RoomActions>
          </S.RoomContent>
        </S.RoomItem>
      ) : (
        <EmptyState
          title={t('packages-settings:packageRoomTab.empty.title')}
          description={t('packages-settings:packageRoomTab.empty.description')}
        />
      )}
    </S.Container>
  )
}
